import React, { useState, useContext, useRef } from "react";
import { registerLocale } from "react-datepicker";
import DatePicker from "../../../components/DatePickerInput";
import "react-datepicker/dist/react-datepicker.css";
import ar from "date-fns/locale/es";
import FuncionesContext, {
  alertarError,
  parseISOString,
  parseHoraString,
  getAtributo,
} from "../../../context/FuncionesContext";
import { useRefDataStore } from "../../../../..";
import { transformarFechaISOaMySQL } from "../../../ABM/componentes/inputs/Fecha_simple";
import { parseFechaFormato } from "../../../helper/funciones";
import Nullifier from "../../../components/Nullifier";

registerLocale("ar", ar);

function esFechaValida(valor) {
  return valor instanceof Date && !Number.isNaN(valor.getTime());
}

function esFormatoSoloHora(formato = "") {
  return /[Hhms]/.test(formato) && !/[dMy]/.test(formato);
}

function tieneZonaHoraria(valor) {
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(valor);
}

function parseFechaLocal(valor) {
  if (typeof valor !== "string" || tieneZonaHoraria(valor)) return null;

  const match = valor.trim().match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,3}))?)?$/,
  );

  if (!match) return null;

  const [, anio, mes, dia, hora = "0", minuto = "0", segundo = "0", ms = "0"] =
    match;
  const fecha = new Date(
    Number(anio),
    Number(mes) - 1,
    Number(dia),
    Number(hora),
    Number(minuto),
    Number(segundo),
    Number(ms.padEnd(3, "0")),
  );

  return esFechaValida(fecha) ? fecha : null;
}

function parseValor(valor, formato) {
  try {
    if (valor === null || valor === undefined || valor === "") return null;
    if (esFechaValida(valor)) return valor;

    const esHora = esFormatoSoloHora(formato);
    const texto = typeof valor === "string" ? valor.trim() : valor;

    if (esHora) {
      if (typeof texto === "string" && tieneZonaHoraria(texto)) {
        const fechaConZona = parseISOString(texto);
        if (esFechaValida(fechaConZona)) return fechaConZona;
      }

      const hora = parseHoraString(texto);
      if (esFechaValida(hora)) return hora;
    }

    const fechaLocal = parseFechaLocal(texto);
    if (esFechaValida(fechaLocal)) return fechaLocal;

    const fecha = parseISOString(valor);
    if (esFechaValida(fecha)) return fecha;

    const hora = parseHoraString(valor);
    if (esFechaValida(hora)) return hora;

    return null;
  } catch (err) {
    console.log(err);
    return null;
  }
}

function normalizarFechaPorFormato(date, formato) {
  if (date === null || date === undefined) return null;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

  const format = formato ?? "";
  const parts = [];

  if (/y/i.test(format)) parts.push(`y:${date.getFullYear()}`);
  if (/M/.test(format)) parts.push(`M:${date.getMonth() + 1}`);
  if (/d/.test(format)) parts.push(`d:${date.getDate()}`);
  if (/[Hh]/.test(format)) parts.push(`h:${date.getHours()}`);
  if (/m/.test(format)) parts.push(`m:${date.getMinutes()}`);
  if (/s/.test(format)) parts.push(`s:${date.getSeconds()}`);

  // Fallback: si el formato no trae tokens esperados, comparar fecha+hora completa.
  if (parts.length === 0) {
    parts.push(`y:${date.getFullYear()}`);
    parts.push(`M:${date.getMonth() + 1}`);
    parts.push(`d:${date.getDate()}`);
    parts.push(`h:${date.getHours()}`);
    parts.push(`m:${date.getMinutes()}`);
    parts.push(`s:${date.getSeconds()}`);
  }

  return parts.join("|");
}

const InputDate = ({
  data,
  cab,
  campokey,
  indiceData,
  id_elemento,
  Context,
  valor,
  updateid,
}) => {
  const { superSubmit } = useContext(FuncionesContext);
  const { opciones } = useContext(Context);

  const [v, setStore] = useRefDataStore((s) => s[data._key + campokey]);
  const [update_id] = useRefDataStore(
    (s) => s[data._key + cab.update_id_alias],
  );

  const formato = cab.fecha_formato ?? "dd-MM-yyyy";
  const validaFormatoManual = /y/.test(formato);
  const {
    showTimeSelect,
    showTimeSelectOnly,
    timeFormat,
    dateFormat,
    timeIntervals,
  } = parseFechaFormato(formato);
  const def = getAtributo("default", data, cab);
  const valorInicial = parseValor((v ?? valor) || def, formato);

  const [value, setValue] = useState(valorInicial);
  const [lastValue, setLastvalue] = useState(valorInicial);
  const [updateId, setUpdateId] = useState(update_id ?? updateid);
  const ingresoManual = useRef({ valor: "", valido: true });

  const permite_null =
    data[cab.id_a + "_permite_null"] === "s" || cab.permite_null === "s";
  const disabled = data[cab.id_a + "_disabled"] === "s" || cab.disabled === "s";

  // nuevo: detectar icono_obligatorio
  const icono_obligatorio =
    data[cab.id_a + "_icono_obligatorio"] === "s" ||
    cab.icono_obligatorio === "s";

  const nombre = (() => {
    if (opciones.tipo.id === 2) return false;
    if (cab.nombre_alias) {
      return data[cab.nombre_alias];
    }
    return cab.nombre;
  })();

  const className = data[cab.id_a + "_className"] ?? cab.className;

  const handleCancelar = () => {
    const valorActual = parseValor((v ?? valor) || def, formato);
    setValue(valorActual);
    setLastvalue(valorActual);
  };

  const handleChangeRaw = (evento) => {
    if (!validaFormatoManual || typeof evento?.target?.value !== "string") {
      return;
    }

    ingresoManual.current = {
      valor: evento.target.value,
      valido: false,
    };
  };

  const handleChange = (date, evento) => {
    if (typeof evento?.target?.value === "string") {
      ingresoManual.current.valido = true;
    } else {
      ingresoManual.current = { valor: "", valido: true };
    }

    setValue(date);
  };

  const handleGuardar = async (date) => {
    const valor = date;
    const nuevoNormalizado = normalizarFechaPorFormato(valor, formato);
    const previoNormalizado = normalizarFechaPorFormato(lastValue, formato);
    if (nuevoNormalizado === previoNormalizado) return;

    setValue(valor);
    if (!permite_null) {
      if (valor === null) return;
    }

    const valorParseado =
      valor === null ? null : transformarFechaISOaMySQL(valor, formato);

    const { id_a } = cab;

    await superSubmit({
      valor: valorParseado,
      id_a,
      update_id: updateId,
      handleCancelar,
      cab,
      data,
      indiceData,
    })
      .then((result) => {
        setLastvalue(valor);
        const parsedValor = valor === null ? null : valor.toISOString();
        setStore({ [data._key + campokey]: parsedValor });
        if (!updateId) {
          setStore({
            [data._key + cab.update_id_alias]: result.data.registro.id,
          });
          setUpdateId(result.data.registro.id);
        }

        return result;
      })
      .catch((err) => {
        console.log("Cancelado ");
        // setValue(lastValue)
      });
  };

  const handleBlur = () => {
    const { valor: valorManual, valido } = ingresoManual.current;

    if (validaFormatoManual && valorManual.trim() !== "" && !valido) {
      alertarError(
        `La fecha ingresada no coincide con el formato requerido: ${dateFormat}.`,
      );
      return;
    }

    ingresoManual.current = { valor: "", valido: true };
    handleGuardar(value);
  };

  return (
    <div
      onBlur={handleBlur}
      id={id_elemento}
      className="tarjeta_grid_item_label_item"
    >
      {nombre ? <label className="vista_label">{nombre}:</label> : null}

      {/* Contenedor para input + icono obligatorio */}
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <DatePicker
          showTimeSelect={showTimeSelect}
          showTimeSelectOnly={showTimeSelectOnly}
          timeFormat={timeFormat}
          dateFormat={dateFormat}
          strictParsing={validaFormatoManual}
          timeIntervals={timeIntervals}
          timeCaption="Hora"
          selected={value}
          fechaMinima={getAtributo("fecha_minima", data, cab)}
          onChange={handleChange}
          onChangeRaw={handleChangeRaw}
          disabled={disabled}
          //isClearable={permite_null}
          locale="ar"
          placeholderText=" sin fecha "
          // withPortal={opciones.tipo.id === 2}
          showYearDropdown
          scrollableYearDropdown
          className={`input_fecha ${className}`}
        />
        {icono_obligatorio && <span className="icono_obligatorio">*</span>}
        {permite_null && !disabled ? (
          <Nullifier
            onClick={() => handleGuardar(null)}
            className="columna_input_date_null"
          />
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

export default InputDate;
